// Tên tiếng Việt của MỘT vai — MỘT bảng duy nhất cho cả app.
//
// ── VẤN ĐỀ TỆP NÀY DỌN ────────────────────────────────────────────────
// Mã vai đã chuẩn và khớp máy chủ; chỗ vỡ là phần HIỂN THỊ. Đo 09/10/2026,
// mỗi màn tự khai một bảng nhãn nên một mã mang nhiều tên:
//
//   admin          → "Admin" (Nhân sự, thanh bên) · "Quản trị" (danh phận)
//   sale           → "Kinh doanh" (Nhân sự, danh phận) — trùng tên PHÒNG
//   qc             → "QC (kiểm soát chất lượng)" · "QC" · "Kiểm Soát Chất
//                    Lượng" · "Kiểm soát chất lượng"
//   coordinator    → "Điều phối (đã khoá 23/08/2026 — 0 quyền)" · "Điều phối
//                    (đã khoá)" · "Điều phối"
//   branch_manager → "Trưởng cửa hàng" · "Trưởng Cửa Hàng"
//   shift_lead     → thiếu hẳn ở màn Nhân sự (in mã thô) · "Trưởng Ca"
//
// ── CHỐT 09/10/2026 ───────────────────────────────────────────────────
// Dùng đúng bộ tên của GNPM (mục "Role mapping" trong README hai kho), thêm
// "(đã khoá)" cho hai vai không còn cấp mới.
//
// ── LUẬT CHO NGƯỜI SỬA SAU ────────────────────────────────────────────
// Thêm vai ở `roles.ts` thì thêm một dòng ở đây — bảng khai kiểu chặt theo
// `Role` nên TypeScript BẮT thiếu. Bản Go là `roleLabels` trong
// `backend/internal/platform/auth/roles.go`; `role-labels.test.ts` đọc thẳng
// tệp Go và đỏ khi hai bên lệch một chữ. Khai tên vai ở tệp khác cũng đỏ.
//
// ── KHÔNG THUỘC TỆP NÀY ───────────────────────────────────────────────
//  · Tên PHÒNG BAN ("Kinh Doanh", "Kế Toán"…) — `features/staff/permissions.ts`.
//    `sale`/`accounting` vừa là mã vai vừa là mã phòng; tên phòng ở bảng kia.
//  · Danh phận (vai × phòng, VD "Trưởng phòng hậu kỳ") — máy chủ dựng ở
//    `auth.PersonaLabel`, từ chính tên vai trong bảng Go.
//  · Chức danh hiện cho KHÁCH ở cổng chat (`customerchat/identity.go`).

import type { Role } from "./roles";

export const ROLE_LABELS: Readonly<Record<Role, string>> = {
  admin: "Tổng Giám đốc",
  director: "Giám đốc",
  manager: "Trưởng phòng",
  coordinator: "Người Điều Phối (đã khoá)",
  accounting: "Kế toán",
  sale: "Sale",
  staff: "Nhân viên",
  viewer: "Chỉ xem",
  branch_manager: "Trưởng cửa hàng",
  area_manager: "Quản lý cụm",
  qc: "QC",
  shift_lead: "Trưởng ca (đã khoá)",
};

/**
 * Tên tiếng Việt của một mã vai. Mã lạ → trả về CHÍNH mã đó (rỗng → `""`):
 * mã thô nhìn thấy được thì có người báo, chữ rỗng thì không.
 */
export function roleLabel(role: string | null | undefined): string {
  const key = (role ?? "").trim();
  if (!key) return "";
  return key in ROLE_LABELS ? ROLE_LABELS[key as Role] : key;
}

/**
 * Tên của MỘT loại QC. Mã vẫn là một vai `qc` cộng một phòng; tên suy từ phòng
 * (QĐ 0503, CEO chốt 3a, 31/10/2026). Phòng `reception` đọc là "QC cơ sở": đi kiểm
 * cơ sở là việc chính của họ, và họ vẫn phụ trách lễ tân. Gương của `QCLabel` ở
 * `backend/internal/platform/auth/persona.go` — `role-labels.test.ts` đọc tệp Go
 * và đỏ khi hai bên lệch.
 */
export function qcLabel(department: string | null | undefined): string {
  const d = (department ?? "").trim().toLowerCase();
  if (!d) return ROLE_LABELS.qc;
  if (d.startsWith("retouch")) return "QC sửa hình";
  if (d.startsWith("photo")) return "QC chụp ảnh";
  if (d === "makeup") return "QC trang điểm";
  if (d === "reception") return "QC cơ sở";
  if (d === "cskh") return "QC CSKH";
  return ROLE_LABELS.qc;
}

/** Loại QC theo phòng — cùng nhánh với `qcLabel` để tên và trang làm việc không lệch (QĐ 0521). */
export type QcKind = "retouch" | "photo" | "makeup" | "facility" | "cskh" | "none";

export function qcKindOf(department: string | null | undefined): QcKind {
  const d = (department ?? "").trim().toLowerCase();
  if (d.startsWith("retouch")) return "retouch";
  if (d.startsWith("photo")) return "photo";
  if (d === "makeup") return "makeup";
  if (d === "reception") return "facility";
  if (d === "cskh") return "cskh";
  return "none";
}

/** Tên vai của một NGƯỜI: như `roleLabel`, riêng QC gọi đúng loại theo phòng. */
export function personRoleLabel(
  role: string | null | undefined,
  department: string | null | undefined,
): string {
  return (role ?? "").trim() === "qc" ? qcLabel(department) : roleLabel(role);
}
